<template>
  <div id="home" class="diagnostic-section ws-grid" :class="sectionClasses">
    <div
      class="diagnostic-section__diagnostic diagnostic-section__content diagnostic"
      :class="diagnosticClasses"
    >
      <h2 class="diagnostic-section__title" :class="titleClasses">{{ title }}</h2>

      <div
        v-if="subtitle"
        class="diagnostic-section__subtitle u-typography-helvetica u-text u-text--m"
      >
        {{ subtitle }}
      </div>

      <div v-if="!isSmallScreen" class="diagnostic-section__formulas formulas">
        <serum-formula
          v-for="(formula, i) of formulas"
          :key="i"
          class="formulas__formula"
          :formula="formula"
          :is-one-of-two-formulas="formulas.length > 1"
        />
      </div>

      <div class="diagnostic-section__products diagnostic-section__products--mobile products">
        <div class="products__recommended u-typography-helvetica u-text u-text--m">
          {{ recommendedProductsTitle }}
        </div>
        <slot name="products"></slot>
      </div>

      <div class="diagnostic-section__buttons buttons">
        <u-button
          class="buttons__button button-primary"
          size="medium"
          @click="$emit('order-prescription')"
          >{{ sectionPrimaryButtonText }}</u-button
        >
        <u-button
          class="buttons__button button-secondary"
          size="medium"
          kind="secondary"
          @click="$emit('check-diagnostic')"
          >{{ sectionSecondaryButtonText }}</u-button
        >
      </div>

      <div v-if="!isSmallScreen" class="diagnostic-section__arrows">
        <scroll-down-sign />
      </div>
    </div>

    <div
      class="diagnostic-section__products diagnostic-section__products--desktop diagnostic-section__content products"
      :class="productsClasses"
    >
      <div class="products__recommended u-typography-helvetica u-text u-text--m">
        {{ recommendedProductsTitle }}
      </div>
      <slot name="products"></slot>
    </div>
  </div>
</template>

<script>
import { UButton } from 'universkin-shared';

import ScrollDownSign from '@/components/diagnostic-section/ScrollDownSign.vue';
import SerumFormula from '@/components/diagnostic-section/SerumFormula.vue';

import { getProductPackItems, getProductPackUsage } from '@/models/core/ProductPack';

import { SERUM_USAGES } from '@/constants/treatment';

export default {
  name: 'DiagnosticSectionLayout',
  components: {
    UButton,
    ScrollDownSign,
    SerumFormula
  },
  props: {
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      required: true
    },
    sectionPrimaryButtonText: {
      type: String,
      required: true
    },
    sectionSecondaryButtonText: {
      type: String,
      required: true
    },
    recommendedProductsTitle: {
      type: String,
      required: true
    },
    serumProduct: {
      type: Object,
      default: null
    },
    singleProduct: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    formulas() {
      if (!this.serumProduct) {
        return [];
      }

      const usage = getProductPackUsage(this.serumProduct);
      const packItems = getProductPackItems(this.serumProduct);

      return usage === SERUM_USAGES.ONE_MORNING_ONE_EVENING ? packItems : [packItems[0]];
    },
    sectionClasses() {
      return {
        'diagnostic-section--zero-formulas': !this.serumProduct,
        'diagnostic-section--single-product': this.singleProduct
      };
    },
    diagnosticClasses() {
      const single = ['ws-col-4-gte600', 'ws-col-5-gte960'];
      const multi = ['ws-col-6-gte600', 'ws-col-5-gte960'];

      return this.singleProduct ? single : multi;
    },
    productsClasses() {
      const single = ['ws-col-4-gte600', 'ws-col-6-gte960'];
      const multi = ['ws-col-6-gte600'];

      return this.singleProduct ? single : multi;
    },
    titleClasses() {
      const width = this.$windowWidth.value;

      if (width >= 1920) {
        return ['u-typography-helvetica', 'u-text-display', 'u-text-display--l'];
      }

      if (width >= 1440) {
        return ['u-typography-helvetica', 'u-text-display', 'u-text-display--m'];
      }

      if (width >= 600) {
        return ['u-typography-helvetica', 'u-text-display', 'u-text-display--s'];
      }

      return ['u-typography-helvetica', 'u-text-display', 'u-text-display--m'];
    },
    isSmallScreen() {
      return this.$windowWidth.value < 960;
    }
  }
};
</script>

<style lang="scss" scoped>
.diagnostic-section {
  position: relative;
  --page-horizontal-padding: var(--ws-grid-m, 16px);
  padding: 24px var(--page-horizontal-padding) 32px var(--page-horizontal-padding);
  overflow-x: hidden;

  &__title {
    margin-bottom: 16px;
  }

  &__subtitle,
  &__formulas {
    margin-bottom: 32px;
  }

  &__products {
    margin-bottom: 32px;

    &--mobile {
      display: block;
    }

    &--desktop {
      display: none;
    }
  }

  &__buttons {
    width: 100%;
  }

  &__arrows {
    display: flex;
    justify-content: center;
    margin-top: 16px;
  }
}

.formulas {
  display: none;

  &__formula:not(:last-child) {
    margin-bottom: 32px;
  }
}

.buttons {
  &__button {
    width: 100%;
  }

  &__button:not(:last-child) {
    margin-bottom: 8px;
  }
}

.products {
  &__recommended {
    margin-bottom: 16px;
    color: var(--u-color-grey-600);
  }
}

@media (max-width: 959px) {
  .diagnostic-section {
    &__arrows {
      display: none;
    }
  }
}

@media (min-width: 600px) {
  .diagnostic-section {
    padding-top: 60px;

    &__content {
      margin-inline-start: auto;
      margin-inline-end: auto;
    }

    &--single-product {
      display: flex;

      .diagnostic-section__diagnostic {
        margin-inline-end: var(--ws-grid-g);
      }

      .diagnostic-section__formulas {
        display: block;
      }

      .diagnostic-section__products {
        &--mobile {
          display: none;
        }

        &--desktop {
          display: block;
        }
      }

      .diagnostic-section__arrows {
        display: none;
      }
    }
  }
}

@media (min-width: 960px) {
  .diagnostic-section {
    display: flex;
    padding-top: 24px;
    border-bottom: 1px solid var(--u-color-grey-200);

    &__content {
      margin-inline-start: 0;
      margin-inline-end: 0;
    }

    &__diagnostic {
      margin-inline-end: calc(var(--ws-grid-col-size) + var(--ws-grid-g) * 2);
    }

    &__formulas {
      display: block;
    }

    &--single-product {
      .diagnostic-section__diagnostic {
        margin-inline-end: calc(var(--ws-grid-col-size) + var(--ws-grid-g) * 2);
      }
    }

    &__products {
      margin-bottom: 0;

      &--mobile {
        display: none;
      }

      &--desktop {
        display: block;
      }
    }
  }

  .diagnostic {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .buttons {
    width: 100%;

    &__button {
      height: 64px;

      &:not(:last-child) {
        margin-bottom: 16px;
      }
    }
  }

  .products {
    &__recommended {
      margin-bottom: 24px;
    }
  }
}

@media (min-width: 1152px) {
  .diagnostic-section {
    &--single-product {
      .diagnostic-section__formulas {
        display: flex;
      }
    }

    &__arrows {
      margin-top: 24px;
    }

    &__subtitle,
    &__formulas {
      margin-bottom: 48px;
    }

    &__arrows {
      margin-top: 32px;
    }
  }

  .formulas {
    display: flex;

    &__formula {
      flex: 1 1 auto;
    }

    &__formula:not(:last-child) {
      margin-bottom: 0;
      margin-inline-end: 32px;
    }
  }

  .buttons {
    display: flex;

    &__button {
      flex: 1 1 auto;
    }

    &__button:not(:last-child) {
      margin-bottom: 0;
      margin-inline-end: 24px;
    }
  }
}

@media (min-width: 1920px) {
  .diagnostic-section {
    padding-top: 32px;
  }
}
</style>
